import React, { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import Button, { ButtonContext } from 'components/styleguide/button/Button';

const styles = require('./Tooltip.module.scss');

const CLOSE_DELAY_MS = 150;
const VIEWPORT_MARGIN = 16;
const GAP = 8;

const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

let tooltipIdCounter = 0;

export type TooltipVariant = 'plain' | 'rich';

type CommonTooltipProps = {
  targetId: string;
  role?: 'tooltip' | 'dialog';
  popoverClassName?: string;
  stopPropagation?: boolean;
  trapFocus?: boolean;
};

type PlainTooltipProps = CommonTooltipProps & {
  variant?: 'plain';
  children: ReactNode;
};

export type TooltipButton = {
  context: ButtonContext;
  label: string;
  onClick?: () => void;
};

type RichTooltipProps = CommonTooltipProps & {
  variant: 'rich';
  title?: ReactNode;
  children: ReactNode;
  button?: TooltipButton;
};

type TooltipProps = PlainTooltipProps | RichTooltipProps;

export default function Tooltip(props: TooltipProps) {
  const {
    children,
    targetId,
    role = 'tooltip',
    popoverClassName,
    stopPropagation = false,
    trapFocus = false,
  } = props;
  const variant: TooltipVariant = props.variant ?? 'plain';
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const [id] = useState(() => `sg-tooltip-${++tooltipIdCounter}`);
  const targetRef = useRef<HTMLElement | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openRef = useRef(open);
  const trapFocusRef = useRef(trapFocus);
  const stopPropagationRef = useRef(stopPropagation);

  const popoverId = `${id}-popover`;

  useEffect(() => { openRef.current = open; }, [open]);
  useEffect(() => { trapFocusRef.current = trapFocus; }, [trapFocus]);
  useEffect(() => { stopPropagationRef.current = stopPropagation; }, [stopPropagation]);

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const show = useCallback(() => {
    clearCloseTimer();
    setOpen(true);
  }, []);

  const hide = useCallback(() => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  }, []);

  useEffect(() => () => clearCloseTimer(), []);

  useEffect(() => {
    const target = document.getElementById(targetId);
    if (!target) {
      // eslint-disable-next-line no-console
      console.warn(`Tooltip: no element with id "${targetId}" found`);
      return undefined;
    }
    targetRef.current = target;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (stopPropagationRef.current && (event.key === ' ' || event.key === 'Enter')) {
        event.stopPropagation();
      }
      if (
        trapFocusRef.current
        && event.key === 'Tab'
        && !event.shiftKey
        && openRef.current
        && popoverRef.current
      ) {
        const firstFocusable = popoverRef.current.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
        if (firstFocusable) {
          event.preventDefault();
          firstFocusable.focus();
        }
      }
    };

    target.addEventListener('mouseenter', show);
    target.addEventListener('mouseleave', hide);
    target.addEventListener('focus', show);
    target.addEventListener('blur', hide);
    target.addEventListener('keydown', handleKeyDown);

    if (role === 'tooltip') {
      target.setAttribute('aria-describedby', popoverId);
    } else {
      target.setAttribute('aria-haspopup', 'dialog');
      target.setAttribute('aria-controls', popoverId);
    }

    return () => {
      target.removeEventListener('mouseenter', show);
      target.removeEventListener('mouseleave', hide);
      target.removeEventListener('focus', show);
      target.removeEventListener('blur', hide);
      target.removeEventListener('keydown', handleKeyDown);
      if (role === 'tooltip') {
        target.removeAttribute('aria-describedby');
      } else {
        target.removeAttribute('aria-haspopup');
        target.removeAttribute('aria-controls');
        target.removeAttribute('aria-expanded');
      }
      targetRef.current = null;
    };
  }, [targetId, role, popoverId, show, hide]);

  useEffect(() => {
    if (role === 'dialog' && targetRef.current) {
      targetRef.current.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
  }, [open, role]);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return undefined;
    }

    const reposition = () => {
      if (!targetRef.current || !popoverRef.current) return;
      const triggerRect = targetRef.current.getBoundingClientRect();
      const popoverRect = popoverRef.current.getBoundingClientRect();

      const idealLeft = triggerRect.left + triggerRect.width / 2 - popoverRect.width / 2;
      const maxLeft = window.innerWidth - popoverRect.width - VIEWPORT_MARGIN;
      const left = Math.max(VIEWPORT_MARGIN, Math.min(idealLeft, maxLeft));

      const aboveTop = triggerRect.top - GAP - popoverRect.height;
      const belowTop = triggerRect.bottom + GAP;
      const top = aboveTop >= VIEWPORT_MARGIN ? aboveTop : belowTop;

      setPosition({ top, left });
    };

    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        targetRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const handlePopoverKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (trapFocus && event.key === 'Tab' && event.shiftKey) {
      event.preventDefault();
      targetRef.current?.focus();
    }
  };

  const handlePopoverClick = (event: React.MouseEvent) => {
    if (stopPropagation) event.stopPropagation();
  };

  const variantClass = variant === 'rich' ? styles.popoverRich : styles.popoverPlain;
  const popoverClasses = [styles.popover, variantClass, popoverClassName].filter(Boolean).join(' ');

  if (!open) return null;

  const titleId = `${id}-title`;
  const richTitle = props.variant === 'rich' ? props.title : undefined;
  const richButton = props.variant === 'rich' ? props.button : undefined;

  return ReactDOM.createPortal(
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      ref={popoverRef}
      id={popoverId}
      className={popoverClasses}
      style={{
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        visibility: position ? 'visible' : 'hidden',
      }}
      role={role}
      aria-labelledby={role === 'dialog' && richTitle ? titleId : undefined}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={trapFocus ? show : undefined}
      onBlur={trapFocus ? hide : undefined}
      onKeyDown={handlePopoverKeyDown}
      onClick={stopPropagation ? handlePopoverClick : undefined}
    >
      {variant === 'rich' ? (
        <>
          {richTitle && <div id={titleId} className={styles.richTitle}>{richTitle}</div>}
          <div className={styles.richBody}>{children}</div>
          {richButton && (
            <div className={styles.richActions}>
              <Button context={richButton.context} type="terceary" size="S" onClick={richButton.onClick}>
                {richButton.label}
              </Button>
            </div>
          )}
        </>
      ) : (
        children
      )}
    </div>,
    document.body,
  );
}
